import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ALLOCATION_SKILLS, SKILL_IDS } from "@/constants/allocations";
import { AllocationSkillId, AllocationSkills, ShiftCapsPayload } from "@/types/allocations";
import { useCallback, useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export interface MissingAgent {
  userId: number;
  name: string;
}

interface MissingAgentsModalProps {
  open: boolean;
  onClose: () => void;
  isSaving: boolean;
  onConfirm: (agents: ShiftCapsPayload[]) => void;
  agents: MissingAgent[];
}

export const MissingAgentsModal = ({ open, onClose, isSaving, onConfirm, agents }: MissingAgentsModalProps) => {
  const [agentCaps, setAgentCaps] = useState<Record<number, AllocationSkills>>({});

  useEffect(() => {
    if (agents.length > 0) {
      const initial: Record<number, AllocationSkills> = {};
      for (const agent of agents) {
        initial[agent.userId] = {};
      }
      setAgentCaps(initial);
    }
  }, [agents]);

  const handleCapChange = useCallback((userId: number, skillId: AllocationSkillId, value: number) => {
    setAgentCaps((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], [skillId]: value },
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    const payload: ShiftCapsPayload[] = agents.map((agent) => ({
      userId: agent.userId,
      shiftStart: null,
      shiftEnd: null,
      allocationSkills: Object.fromEntries(SKILL_IDS.map((id) => [String(id), agentCaps[agent.userId]?.[id] ?? 0])),
    }));
    onConfirm(payload);
  }, [agents, agentCaps, onConfirm]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Missing Agents</DialogTitle>
          <DialogDescription>
            The following agents are missing from the current allocations data. Set their caps and they will be added
            before retrying.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] grow overflow-auto">
          <Accordion type="multiple" defaultValue={agents.map((a) => String(a.userId))}>
            {agents.map((agent) => (
              <AccordionItem key={agent.userId} value={String(agent.userId)}>
                <AccordionTrigger>{agent.name}</AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-4">
                    {SKILL_IDS.map((skillId) => (
                      <div key={skillId} className="flex items-center justify-between rounded-xl border p-4 shadow-xs">
                        <Label htmlFor={`cap-${agent.userId}-${skillId}`} className="text-sm">
                          {ALLOCATION_SKILLS[skillId]}
                        </Label>
                        <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-1">
                          <Button
                            className="text-muted-foreground text-xl"
                            variant="ghost"
                            size="icon"
                            disabled={isSaving || (agentCaps[agent.userId]?.[skillId] ?? 0) <= 0}
                            onClick={() =>
                              handleCapChange(
                                agent.userId,
                                skillId,
                                Math.max(0, (agentCaps[agent.userId]?.[skillId] ?? 0) - 1)
                              )
                            }
                          >
                            -
                          </Button>
                          <Input
                            id={`cap-${agent.userId}-${skillId}`}
                            type="number"
                            min={0}
                            value={agentCaps[agent.userId]?.[skillId] ?? 0}
                            onChange={(e) => handleCapChange(agent.userId, skillId, parseInt(e.target.value, 10) || 0)}
                            disabled={isSaving}
                            className="h-fit w-8 rounded-none border-none p-0 text-center text-lg! font-semibold shadow-none focus-visible:ring-0"
                          />
                          <Button
                            className="text-muted-foreground text-xl"
                            variant="ghost"
                            size="icon"
                            disabled={isSaving}
                            onClick={() =>
                              handleCapChange(agent.userId, skillId, (agentCaps[agent.userId]?.[skillId] ?? 0) + 1)
                            }
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Close
          </Button>
          <Button onClick={handleConfirm} disabled={isSaving}>
            Add Agents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
